import { createClient } from '@supabase/supabase-js'
import { ERREURS_CONTACT, phraseErreurFonction } from './commun'
import { ERREURS_CASQUETTES } from './casquettes'
import { CLE_PUBLISHABLE, URL_SUPABASE } from './projet'

/**
 * Le MÊME projet Supabase que les autres surfaces SwiftDeliver (console
 * opérateur, portail SaaS, app livreur, supervision).
 *
 * Adresse et clé publique : `./projet.ts` (valeur par défaut = production actuelle,
 * remplaçable au build par `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY`).
 * ⚠️ Le projet cloud a les clés JWT **legacy désactivées** : seule la clé
 * `publishable` y fonctionne. Elle est publique par conception — ce qu'un porteur
 * peut lire ou écrire est décidé uniquement par les policies RLS
 * (`is_org_member` / `has_org_role`). Aucune clé `service_role` ni `sb_secret_`
 * ne doit apparaître : elle contournerait la RLS et se retrouverait dans le
 * bundle JavaScript, donc entre toutes les mains.
 */
export { CLE_PUBLISHABLE, URL_SUPABASE }

export const supabase = createClient(URL_SUPABASE, CLE_PUBLISHABLE, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    /* « Continuer avec Google » : le retour d'OAuth arrive avec `?code=…`
       (flux PKCE — aucun jeton dans l'URL). supabase-js l'échange contre une
       session au démarrage et nettoie l'URL ; `getSession()` attend cet
       échange. Même réglage que la console SwiftEats. */
    flowType: 'pkce',
    detectSessionInUrl: true,
  },
})

/**
 * Erreur d'edge function, rendue lisible.
 *
 * supabase-js remonte « Edge Function returned a non-2xx status code » — une
 * phrase qui ne dit ni ce qui a échoué ni pourquoi, alors que la fonction a
 * répondu une raison précise dans le corps. C'est ce que faisait `edgeMsg()`
 * dans la version HTML ; on garde le comportement et on ajoute la traduction.
 */
export const LISIBLE: Record<string, string> = {
  email_already_used: "Cet e-mail a déjà un compte.",
  invalid_email: "Adresse e-mail invalide.",
  compte_autre_role: "Cette adresse est déjà utilisée par un compte d'un autre type (équipe, livreur ou marchand). Utilisez une autre adresse.",
  deja_livreur: "Ce compte est déjà livreur.",
  deja_gerant: "Ce compte gère déjà un autre établissement.",
  forbidden: "Vous n'avez pas le droit d'effectuer cette action.",
  forbidden_for_role: "Votre rôle ne permet pas cette action.",
  unauthorized: "Session expirée — reconnectez-vous.",
  invalid_transition: "Cette course n'est plus dans l'état attendu. Rechargez la page.",
  driver_busy: "Ce livreur a déjà une course en cours.",
  not_org_member: "Vous n'appartenez pas à cette organisation.",
  conflict: "Quelqu'un a modifié cette course en même temps que vous. Rechargez la page.",
  proof_required:
    "Votre organisation exige une photo de remise : seule l'app Livreur peut clore cette course (le livreur photographie la remise).",
  delivery_code_mismatch: "Cette course a un code de remise : seule l'app Livreur peut la clore, avec le code donné par le destinataire.",
  plan_required: "Module non inclus dans le palier de l'organisation.",
  invalid_amount: 'Montant invalide.',
  unknown_category: 'Catégorie de dépense inconnue.',
  missing_params: 'Champ obligatoire manquant (pour un compte : un e-mail ou un numéro WhatsApp au moins).',
  /* Connexion par numéro : invalid_phone, identifiants_differents, phone_already_used, comptes_multiples. */
  ...ERREURS_CONTACT,
  /* Un compte, plusieurs casquettes (0045) : deja_membre, livreur_client_de_la_course, dernier_proprietaire… */
  ...ERREURS_CASQUETTES,
}

/** Le corps envoyé aux edge functions est toujours un objet JSON simple. */
export type CorpsFonction = Record<string, unknown>

export async function appelerFonction<T>(nom: string, corps: CorpsFonction): Promise<T> {
  const { data, error } = await supabase.functions.invoke(nom, { body: corps })

  if (error) {
    let code = ''
    let detail = ''
    try {
      const contexte = (error as { context?: { json?: () => Promise<unknown> } }).context
      const json = (await contexte?.json?.()) as { error?: string; detail?: unknown; message?: unknown } | undefined
      code = json?.error ?? ''
      /* `detail` d'abord ; à défaut, `message` : `gps-commande` répond `{error, message}` (la phrase française
         est dans `message`) et ne la recopie dans `detail` que depuis la 0052. */
      detail = typeof json?.detail === 'string' ? json.detail : typeof json?.message === 'string' ? json.message : ''
    } catch {
      /* Le corps n'était pas du JSON : on garde le message brut plutôt que rien. */
    }
    throw new Error(phraseErreurFonction(code, detail, LISIBLE, error.message || `${nom} a échoué`))
  }

  /* ⚠️ Une edge function peut répondre 200 AVEC un `error` dans le corps.
     La version HTML testait ce cas pour `create-driver-account` seulement ;
     ici on le teste pour TOUTES, sinon un refus passerait pour un succès et
     l'écran afficherait une course avancée qui ne l'est pas. */
  const reponse = data as { error?: string; detail?: unknown; message?: unknown } | null
  if (reponse && typeof reponse === 'object' && reponse.error) {
    const detail = typeof reponse.detail === 'string' ? reponse.detail : typeof reponse.message === 'string' ? reponse.message : ''
    throw new Error(phraseErreurFonction(reponse.error, detail, LISIBLE))
  }
  return data as T
}
